import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'convex/react'
import { usePaginatedQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { Id } from '../../convex/_generated/dataModel'
import { Header } from '../components/layout/Header'
import { formatDuration } from '../utils/formatDuration'
import { formatDateTime, getTimezoneLabel } from '../utils/formatDateTime'

/**
 * ActivityHistory - Full call history page with pagination
 */
export function ActivityHistory() {
  const { t } = useTranslation()
  const { lovedOneId } = useParams<{ lovedOneId: string }>()

  const timezoneData = useQuery(api.dashboard.getCaregiverTimezone)

  const { results, status, loadMore } = usePaginatedQuery(
    api.activity.getCallHistory,
    lovedOneId ? { lovedOneId: lovedOneId as Id<'lovedOnes'> } : 'skip',
    { initialNumItems: 20 }
  )

  const containerStyles: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const contentWrapperStyles: React.CSSProperties = {
    padding: '1.5rem 1rem',
    maxWidth: '600px',
    margin: '0 auto',
  }

  const backLinkStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#0066cc',
    textDecoration: 'none',
    fontSize: '14px',
    marginBottom: '1rem',
    minHeight: '44px',
  }

  const titleStyles: React.CSSProperties = {
    fontSize: 'clamp(20px, 4vw, 28px)',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '0.5rem',
  }

  const timezoneNoteStyles: React.CSSProperties = {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '1.5rem',
  }

  const listStyles: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  }

  const activityItemStyles: React.CSSProperties = {
    padding: '1rem',
    borderBottom: '1px solid #f3f4f6',
  }

  const activityHeaderStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.25rem',
  }

  const directionStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  }

  const timeStyles: React.CSSProperties = {
    fontSize: '12px',
    color: '#9ca3af',
  }

  const summaryStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: 1.4,
    marginTop: '0.5rem',
  }

  const noActivityStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#9ca3af',
  }

  const loadMoreButtonStyles: React.CSSProperties = {
    width: '100%',
    padding: '1rem',
    backgroundColor: '#f3f4f6',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: '#0066cc',
    minHeight: '44px',
  }

  const loadingStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '2rem',
    color: '#666',
  }

  const timezone = timezoneData?.timezone || 'UTC'
  const timezoneLabel = timezoneData?.label || getTimezoneLabel(timezone)

  if (!lovedOneId) {
    return (
      <div style={containerStyles}>
        <Header />
        <div style={contentWrapperStyles}>
          <p>Invalid loved one ID</p>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyles}>
      <Header />
      <div style={contentWrapperStyles}>
        <Link to={`/dashboard/${lovedOneId}`} style={backLinkStyles}>
          ← Back to Dashboard
        </Link>

        <h1 style={titleStyles}>{t('activity.title')}</h1>
        <p style={timezoneNoteStyles}>{t('activity.timezoneNote', { timezone: timezoneLabel })}</p>

        {status === 'LoadingFirstPage' ? (
          <p style={loadingStyles}>{t('common.loading')}</p>
        ) : results.length === 0 ? (
          <div style={noActivityStyles}>
            <p>{t('activity.noActivity')}</p>
          </div>
        ) : (
          <div style={listStyles}>
            {results.map(call => (
              <div key={call._id} style={activityItemStyles}>
                <div style={activityHeaderStyles}>
                  <div style={directionStyles}>
                    {call.direction === 'incoming' ? '📥' : '📤'}
                    <span>
                      {call.direction === 'incoming'
                        ? t('activity.incoming')
                        : t('activity.outgoing')}
                    </span>
                    {call.duration && (
                      <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                        • {formatDuration(call.duration)}
                      </span>
                    )}
                  </div>
                  <span style={timeStyles}>{formatDateTime(call.startedAt, timezone)}</span>
                </div>
                {call.summary && <p style={summaryStyles}>{call.summary}</p>}
                {!call.summary && (
                  <p style={{ ...summaryStyles, fontStyle: 'italic' }}>
                    {t('activity.callSummaryUnavailable')}
                  </p>
                )}
              </div>
            ))}

            {status === 'CanLoadMore' && (
              <button onClick={() => loadMore(20)} style={loadMoreButtonStyles}>
                {t('activity.loadMore')}
              </button>
            )}

            {status === 'LoadingMore' && (
              <div style={{ ...loadingStyles, padding: '1rem' }}>{t('common.loading')}</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
